import { z } from 'zod';
import { CompanySizeSchema } from './generated';

const MAX_PROFILE_TEXT_LENGTH = 2000;
const MAX_PROFILE_ITEM_LENGTH = 200;
const MAX_PROFILE_LIST_ITEMS = 50;

const normalizeStringList = (items: string[]): string[] =>
  items.map((item) => item.trim()).filter((item) => item.length > 0);

const SurfaceProfileListItemSchema = z
  .string()
  .trim()
  .max(MAX_PROFILE_ITEM_LENGTH)
  .describe('A normalized business profile list entry.');

const SurfaceProfileEntityNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(MAX_PROFILE_ITEM_LENGTH)
  .describe('A non-empty entity name.');

const SurfaceProfileEntityDetailSchema = z
  .string()
  .trim()
  .max(MAX_PROFILE_TEXT_LENGTH)
  .nullable()
  .optional()
  .describe('Optional entity detail text.');

const SurfaceProfileEntityLocationSchema = z
  .string()
  .trim()
  .max(MAX_PROFILE_ITEM_LENGTH)
  .nullable()
  .optional()
  .describe('Optional entity location.');

const SurfaceProfileEntityInputSchema = z.object({
  name: SurfaceProfileEntityNameSchema.describe(
    'Entity legal or commonly used name.',
  ),
  description: SurfaceProfileEntityDetailSchema.describe(
    'Short description of the entity.',
  ),
  location: SurfaceProfileEntityLocationSchema.describe(
    'Entity primary location.',
  ),
});

export const SurfaceBusinessProfileSubsidiaryInputSchema =
  SurfaceProfileEntityInputSchema;

export const SurfaceBusinessProfileDivisionInputSchema =
  SurfaceProfileEntityInputSchema;

export const SurfaceBusinessProfileInputSchema = z.object({
  description: z
    .string()
    .trim()
    .max(MAX_PROFILE_TEXT_LENGTH)
    .describe('Brief company overview.')
    .nullable()
    .optional(),
  primaryIndustry: z
    .string()
    .trim()
    .max(MAX_PROFILE_ITEM_LENGTH)
    .describe('Primary industry or sector.')
    .nullable()
    .optional(),
  locations: z
    .array(SurfaceProfileListItemSchema.describe('Operational location.'))
    .max(MAX_PROFILE_LIST_ITEMS)
    .transform(normalizeStringList)
    .describe('Key operational locations.')
    .optional(),
  companySize: CompanySizeSchema.describe(
    'Estimated company size category: SMALL, MID, LARGE, ENTERPRISE.',
  )
    .nullable()
    .optional(),
  brandsServicesProducts: z
    .array(
      SurfaceProfileListItemSchema.describe('Key product, service, or brand.'),
    )
    .max(MAX_PROFILE_LIST_ITEMS)
    .transform(normalizeStringList)
    .describe('Notable brands, services, or products.')
    .optional(),
  subsidiaries: z
    .array(SurfaceBusinessProfileSubsidiaryInputSchema)
    .max(MAX_PROFILE_LIST_ITEMS)
    .describe('Known subsidiaries with optional details.')
    .optional(),
  divisions: z
    .array(SurfaceBusinessProfileDivisionInputSchema)
    .max(MAX_PROFILE_LIST_ITEMS)
    .describe('Known divisions with optional details.')
    .optional(),
});

export type SurfaceBusinessProfileInput = z.infer<
  typeof SurfaceBusinessProfileInputSchema
>;
